/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable } from '@angular/core';
import {
  FacGraphqlService,
  FacHttpClientService,
  FacQueryParameter,
  FacQueryStatement,
  FacTimeRange,
} from '@foglight/angular-common';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import {
  AlarmTemplateCreateData,
  AlarmTemplateData,
  AlarmTemplateDetailInfo,
  ControllingDomainRoot,
  DefaultTemplateSettings,
  DomainObjectCount,
  DomainRootTypes,
  DomainRuleCount,
  DomainTemplate,
  MultiSeverityRuleConfig,
  RuleConfigUpdateInfo,
  TargetObject,
  TargetTemplateInfo,
  TopologySubDomain,
} from '../_models/alarm-template-ngrx';
import moment from 'moment';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import {
  selectCurrentAlarmTemplate,
  selectDomainRuleCounts,
} from '../_store/alarm-template/alarm-template.selectors';
import { Store } from '@ngrx/store';
import { Connection } from '../_models/connection';
import { flatMap } from 'lodash-es';
import { ALARM_ENDPOINTS } from '../_models/alarm-endpoints';

interface FullAssignedTargetsResponse {
  targets: TargetObject[];
}

interface AlarmTemplateUpdateResponse {
  success: boolean;
  message?: string;
  data?: AlarmTemplateData;
}

type SelectedConnectionsSubject = BehaviorSubject<string[]>;

/**
 * HTTP service for managing alarm template operations.
 * Handles CRUD operations, target assignments, rule configurations, and domain-related queries.
 */
@Injectable({
  providedIn: 'root',
})
export class AlarmTemplatesHttpService {
  selectedAlaramTeplateManageNotification!: AlarmTemplateData;
  public readonly destroyAlarmTemplatesHttp$ = new Subject<void>();

  constructor(
    private http: FacHttpClientService,
    private graphqlService: FacGraphqlService,
    private store: Store,
  ) {}

  /**
   * Retrieves the list of all alarm templates.
   */
  getAlarmTemplateData(): Observable<AlarmTemplateData[]> {
    return this.http.get<AlarmTemplateData[]>(
      ALARM_ENDPOINTS.GET_ALARM_TEMPLATES,
    );
  }

  /**
   * Creates a new alarm template with the provided configuration.
   */
  addNewAlarmTemplate(
    data: AlarmTemplateCreateData,
  ): Observable<AlarmTemplateData[]> {
    return this.http.post(ALARM_ENDPOINTS.ADD_NEW_ALARM_TEMPLATE, data);
  }

  /**
   * Deletes an alarm template by its ID.
   */
  deleteAlarmTemplate(id: string): Observable<void> {
    return this.http.delete(ALARM_ENDPOINTS.DELETE_ALARM_TEMPLATE(id));
  }

  /**
   * Clones an existing alarm template with new configuration data.
   */
  cloneAlarmTemplate(
    id: string,
    data: AlarmTemplateCreateData,
  ): Observable<AlarmTemplateData> {
    return this.http.post(ALARM_ENDPOINTS.CLONE_ALARM_TEMPLATE(id), data);
  }

  /**
   * Updates an existing alarm template with new configuration.
   */
  updateAlarmTemplate(
    id: string,
    data: AlarmTemplateCreateData,
  ): Observable<AlarmTemplateUpdateResponse> {
    return this.http.put(ALARM_ENDPOINTS.UPDATE_ALARM_TEMPLATE(id), data);
  }

  /**
   * Sets default alarm templates for specified domains.
   */
  setDefaults(
    defaultTemplateSettings: DefaultTemplateSettings[],
  ): Observable<void> {
    return this.http.post(
      ALARM_ENDPOINTS.SET_DEFAULTS,
      defaultTemplateSettings,
    );
  }

  /**
   * Retrieves detailed information for a specific alarm template.
   */
  getAlarmTemplateDetail(
    templateId: string,
  ): Observable<AlarmTemplateDetailInfo> {
    return this.http.get<AlarmTemplateDetailInfo>(
      ALARM_ENDPOINTS.GET_ALARM_TEMPLATE_DETAIL(templateId),
    );
  }

  /**
   * Retrieves all topology domains that have alarm rules configured.
   * Filters out excluded domains and domains without rules.
   */
  getTemplateDomains(): Observable<TopologySubDomain[]> {
    const queryParameter = this._createQueryParameter();
    const domainStatement = FacQueryStatement.standard()
      .withField('TopologyDomain')
      .withSimpleSubFields(
        'uniqueId',
        'name',
        'displayName',
        'fullyQualifiedName',
      )
      .withQueryParameter(queryParameter);

    const excludedDomainList = ['forge.dependency', 'vCloud.Dependencie'];
    return this.store.select(selectDomainRuleCounts).pipe(
      takeUntil(this.destroyAlarmTemplatesHttp$),
      switchMap((domainRuleCounts) => {
        return this.graphqlService
          .query<TopologySubDomain>(domainStatement)
          .pipe(
            map((it) => it?.instances),
            map((domain) => {
              // Filter domains based on the domain rule counts and exclusion list
              const result = domain.filter((obj) => {
                const isContainRule =
                  domainRuleCounts &&
                  domainRuleCounts.findIndex(
                    (it) =>
                      it.domainName === obj.fullyQualifiedName &&
                      it.ruleCount > 0,
                  ) >= 0;

                return (
                  isContainRule &&
                  !excludedDomainList.includes(obj.fullyQualifiedName)
                );
              });

              return result;
            }),
          );
      }),
    );
  }

  /**
   * Retrieves rule counts for all domains.
   */
  getDomainRuleCounts(): Observable<DomainRuleCount[]> {
    const domainRuleCounts = this.http.get<Record<string, DomainRuleCount[]>>(
      ALARM_ENDPOINTS.GET_DOMAIN_RULE_COUNTS,
    );
    if (domainRuleCounts) {
      return domainRuleCounts.pipe(map((it) => it?.domainCountInfoList));
    }
    return of([]);
  }

  /**
   * Creates a query parameter with a time range for GraphQL queries.
   */
  private _createQueryParameter(): FacQueryParameter {
    const queryParameter = new FacQueryParameter(
      new FacTimeRange(
        moment().subtract(1, 'hour').toISOString(),
        moment().add(1, 'hour').toISOString(),
      ),
    ).appendLimit(100000);
    return queryParameter;
  }

  /**
   * Sets the currently selected alarm template for notification management.
   */
  setCurrentAlaramTemplate(selectedItem: AlarmTemplateData): void {
    this.selectedAlaramTeplateManageNotification = selectedItem;
  }

  /**
   * Retrieves the currently selected alarm template for notification management.
   */
  getCurrentAlaramTemplate(): AlarmTemplateData | null {
    return this.selectedAlaramTeplateManageNotification;
  }

  /**
   * Retrieves the full list of targets assigned to a specific alarm template and domain.
   */
  getFullAssignedTargets(
    alarmTemplateId: string,
    domainName: string,
  ): Observable<FullAssignedTargetsResponse> {
    return this.http.get(
      ALARM_ENDPOINTS.GET_FULL_ASSIGNED_TARGETS(alarmTemplateId, domainName),
    );
  }

  /**
   * Retrieves object counts for specified domains.
   */
  getDomainObjectCounts(domains: string[]): Observable<DomainObjectCount[]> {
    return this.http
      .post<
        Record<string, DomainObjectCount[]>
      >(ALARM_ENDPOINTS.GET_DOMAIN_OBJECT_COUNTS, { domainNames: domains })
      .pipe(map((it) => it?.domainCountInfoList));
  }

  /**
   * Retrieves rule configurations for a specific alarm template and domain.
   */
  getRuleConfigs(
    alarmTemplateId: string,
    domainName: string,
  ): Observable<MultiSeverityRuleConfig[]> {
    return this.http
      .get<
        Record<string, MultiSeverityRuleConfig[]>
      >(ALARM_ENDPOINTS.GET_RULE_CONFIGS(alarmTemplateId, domainName))
      .pipe(map((it) => it?.ruleConfigs));
  }

  /**
   * Retrieves the current alarm templates assigned to specified targets.
   */
  getTargetsCurrentAlarmTemplates(
    targetIds: string[],
  ): Observable<TargetTemplateInfo[]> {
    return this.http
      .post<
        Record<string, TargetTemplateInfo[]>
      >(ALARM_ENDPOINTS.GET_TARGETS_CURRENT_ALARM_TEMPLATES, { objectIds: targetIds })
      .pipe(map((it) => it?.TargetTemplateInfos));
  }

  /**
   * Retrieves all targets for a specific domain.
   * Filters out targets with the 'AlarmTemplateTargetFiltered' annotation.
   */
  getDomainTargets(domain: string): Observable<Connection[]> {
    const queryParameter = this._createQueryParameter();
    const excludedTargetAnnotation = 'AlarmTemplateTargetFiltered';
    return this.getDomainRootTypes(domain).pipe(
      switchMap((obj) => {
        // Check if rootTypes is empty
        if (!obj.rootTypes || obj.rootTypes.length === 0) {
          return of([]); // Return empty array Observable
        }

        return combineLatest(
          obj.rootTypes.map((type) => {
            const statement = FacQueryStatement.standard()
              .withField(type.typeName)
              .withQueryParameter(queryParameter)
              .withSimpleSubFields(
                'uniqueId',
                'name',
                'topologyTypeName',
                'annotations',
              )
              .startWithObjectSubField('monitoredHost')
              .withSimpleSubFields('name')
              .endObjectSubField();
            return this.graphqlService.query<Connection>(statement).pipe(
              map((it) =>
                it?.instances?.filter((target) => {
                  target.topologyTypeDisplayName = type.typeDisplayName;
                  return !target?.annotations?.includes(
                    excludedTargetAnnotation,
                  );
                }),
              ),
            );
          }),
        );
      }),
      map((it) => flatMap(it)),
      // Add catchError to handle any errors
      catchError((error) => {
        console.error('Error in getDomainTargets:', error);
        return of([]); // Return empty array on error
      }),
    );
  }

  /**
   * Retrieves root types for a specific domain.
   */
  getDomainRootTypes(domain: string): Observable<DomainRootTypes> {
    return this.http.get<DomainRootTypes>(
      ALARM_ENDPOINTS.GET_DOMAIN_ROOT_TYPES(domain),
    );
  }

  /**
   * Assigns selected targets to the current alarm template.
   */
  public assignTargetsToTemplate(
    selectedConnections$: SelectedConnectionsSubject,
  ): Observable<AlarmTemplateData> {
    return this.store.select(selectCurrentAlarmTemplate).pipe(
      switchMap((currentAlarmTemplate: AlarmTemplateData) => {
        const alarmTemplateId = currentAlarmTemplate.id;
        const selectedConnections = selectedConnections$?.value;
        return this.http.post<AlarmTemplateData>(
          ALARM_ENDPOINTS.ASSIGN_TARGETS_TO_TEMPLATE(alarmTemplateId),
          selectedConnections,
        );
      }),
    );
  }

  /**
   * Assigns a single target to the current alarm template.
   */
  public assignTarget(
    selectedConnections$: SelectedConnectionsSubject,
  ): Observable<AlarmTemplateData> {
    return this.store.select(selectCurrentAlarmTemplate).pipe(
      switchMap((currentAlarmTemplate: AlarmTemplateData) => {
        const alarmTemplateId = currentAlarmTemplate.id;
        const selectedConnections = selectedConnections$?.value;
        return this.http.post<AlarmTemplateData>(
          ALARM_ENDPOINTS.ASSIGN_TARGETS_TO_TEMPLATE(alarmTemplateId),
          { objectIds: selectedConnections },
        );
      }),
    );
  }

  /**
   * Unassigns selected targets from the current alarm template.
   */
  unassignedTargets(
    selectedConnections$: SelectedConnectionsSubject,
  ): Observable<AlarmTemplateData> {
    return this.store.select(selectCurrentAlarmTemplate).pipe(
      switchMap((currentAlarmTemplate: AlarmTemplateData) => {
        const alarmTemplateId = currentAlarmTemplate.id;
        const selectedConnections = selectedConnections$?.value;
        return this.http.post<AlarmTemplateData>(
          ALARM_ENDPOINTS.UNASSIGN_TARGETS(alarmTemplateId),
          { objectIds: selectedConnections },
        );
      }),
    );
  }

  /**
   * Retrieves assigned targets for a specific alarm template and domain.
   */
  getAssignedTargets(
    alarmTemplateId: string,
    domainName: string,
  ): Observable<TargetObject[]> {
    return this.http
      .get<
        Record<string, TargetObject[]>
      >(`/api/v1/alarm-templates/${alarmTemplateId}/${domainName}/assigned-targets`)
      .pipe(map((it) => it.targets));
  }

  /**
   * Updates the status of rule configurations for a specific alarm template and domain.
   */
  updateRuleConfigStatus(
    alarmTemplateId: string,
    domainName: string,
    ruleConfigUpdateInfos: RuleConfigUpdateInfo[],
  ): Observable<DomainTemplate> {
    return this.getTemplateDomains().pipe(
      map((it) => it?.find((obj) => obj.name.toLowerCase() === domainName)),
      switchMap((it) =>
        this.http.post<DomainTemplate>(
          `/api/v1/alarm-templates/${alarmTemplateId}/${it?.fullyQualifiedName}/update`,
          { ruleConfigs: ruleConfigUpdateInfos },
        ),
      ),
    );
  }

  /**
   * Updates a specific rule configuration for an alarm template and domain.
   */
  updateRuleConfig(
    alarmTemplateId: string,
    domainName: string,
    ruleId: string,
    data: MultiSeverityRuleConfig,
  ): Observable<MultiSeverityRuleConfig> {
    return this.getTemplateDomains().pipe(
      map((it) => it?.find((obj) => obj.name.toLowerCase() === domainName)),
      switchMap((it) =>
        this.http.put<MultiSeverityRuleConfig>(
          `/api/v1/alarm-templates/${alarmTemplateId}/${it?.fullyQualifiedName}/${ruleId}`,
          data,
        ),
      ),
    );
  }

  /**
   * Retrieves the controlling domain root for specified targets.
   */
  getControllingDomainRoot(
    targetIds: string[],
  ): Observable<ControllingDomainRoot[]> {
    return this.http
      .post<
        Record<string, ControllingDomainRoot[]>
      >(`/api/v1/alarm-templates/targets/controlling-domain-root`, { objectIds: targetIds })
      .pipe(map((it) => it?.controllingDomainRootList));
  }

  /**
   * Unassigns targets from a specific alarm template by connection IDs.
   */
  unassignedTargetsConnection(
    templateId: string,
    ids: string[],
  ): Observable<AlarmTemplateData> {
    return this.http.post<AlarmTemplateData>(
      `/api/v1/alarm-templates/${templateId}/unassign-targets`,
      { objectIds: ids },
    );
  }

  /**
   * Cleanup lifecycle hook that completes all subscriptions.
   */
  ngOnDestroy(): void {
    this.destroyAlarmTemplatesHttp$.next();
    this.destroyAlarmTemplatesHttp$.complete();
  }
}
